import { LucideIcon, Zap, Activity, Atom, Calculator, Gauge, Battery, Waves, Thermometer, CircuitBoard, Ruler, FlaskConical, Droplet, TestTube, Beaker, Wind, Lightbulb, Dna, Triangle, BarChart3, Percent, PieChart, Grid3x3, Sigma, AlignLeft, TrendingUp, Compass, DollarSign, PiggyBank, CreditCard, LineChart, TrendingDown, Building, Coins, Wallet, Calculator as CalcIcon, Scale, Flame, Target, Receipt, Clock, Calendar, Cake, Timer, Fuel, ChefHat, Footprints } from "lucide-react";

export interface CalculatorData {
  id: string;
  title: string;
  description: string;
  category: "physics" | "chemistry" | "math" | "tools" | "finance" | "general";
  subcategory?: string;
  href: string;
  icon: LucideIcon;
  tags: string[];
}

export const calculators: CalculatorData[] = [
  // Physics Calculators
  {
    id: "newtons-law",
    title: "Newton's Law Calculator",
    description: "Calculate force, mass, and acceleration using F=ma",
    category: "physics",
    subcategory: "Mechanics",
    href: "/calculator/newtons-law",
    icon: Zap,
    tags: ["force", "mass", "acceleration", "newton", "mechanics", "f=ma"]
  },
  {
    id: "ohms-law",
    title: "Ohm's Law Calculator",
    description: "Find voltage, current, or resistance in electrical circuits",
    category: "physics",
    subcategory: "Electricity",
    href: "/calculator/ohms-law",
    icon: Battery,
    tags: ["voltage", "current", "resistance", "ohm", "electricity", "circuit"]
  },
  {
    id: "projectile-motion",
    title: "Projectile Motion Calculator",
    description: "Calculate trajectory, range, and time of flight for projectiles",
    category: "physics",
    subcategory: "Mechanics",
    href: "/calculator/projectile-motion",
    icon: Activity,
    tags: ["projectile", "trajectory", "motion", "parabola", "range", "velocity"]
  },
  {
    id: "work-energy",
    title: "Work-Energy Calculator",
    description: "Calculate work done and energy changes",
    category: "physics",
    subcategory: "Energy",
    href: "/calculator/work-energy",
    icon: Zap,
    tags: ["work", "energy", "power", "joules", "kinetic", "potential"]
  },
  {
    id: "kinematics",
    title: "Kinematics Calculator",
    description: "Solve motion problems with kinematic equations",
    category: "physics",
    subcategory: "Mechanics",
    href: "/calculator/kinematics",
    icon: Gauge,
    tags: ["motion", "velocity", "acceleration", "displacement", "time", "kinematics"]
  },
  {
    id: "force",
    title: "Force Calculator",
    description: "Calculate net force and components",
    category: "physics",
    subcategory: "Mechanics",
    href: "/calculator/force",
    icon: Zap,
    tags: ["force", "vector", "components", "net force", "newton"]
  },
  {
    id: "work",
    title: "Work Calculator",
    description: "Calculate work done by a force",
    category: "physics",
    subcategory: "Energy",
    href: "/calculator/work",
    icon: Activity,
    tags: ["work", "force", "displacement", "energy", "joules"]
  },
  {
    id: "power",
    title: "Power Calculator",
    description: "Calculate power from work and time",
    category: "physics",
    subcategory: "Energy",
    href: "/calculator/power",
    icon: Zap,
    tags: ["power", "work", "time", "watts", "energy"]
  },
  {
    id: "kinetic-energy",
    title: "Kinetic Energy Calculator",
    description: "Calculate the energy of motion",
    category: "physics",
    subcategory: "Energy",
    href: "/calculator/kinetic-energy",
    icon: Activity,
    tags: ["kinetic", "energy", "velocity", "mass", "motion"]
  },
  {
    id: "potential-energy",
    title: "Potential Energy Calculator",
    description: "Calculate gravitational potential energy",
    category: "physics",
    subcategory: "Energy",
    href: "/calculator/potential-energy",
    icon: Zap,
    tags: ["potential", "energy", "gravity", "height", "mass"]
  },
  {
    id: "momentum",
    title: "Momentum Calculator",
    description: "Calculate momentum and impulse",
    category: "physics",
    subcategory: "Mechanics",
    href: "/calculator/momentum",
    icon: Activity,
    tags: ["momentum", "impulse", "velocity", "mass", "collision"]
  },
  {
    id: "acceleration",
    title: "Acceleration Calculator",
    description: "Calculate acceleration from velocity and time",
    category: "physics",
    subcategory: "Mechanics",
    href: "/calculator/acceleration",
    icon: Gauge,
    tags: ["acceleration", "velocity", "time", "motion", "rate"]
  },
  {
    id: "speed-velocity",
    title: "Speed & Velocity Calculator",
    description: "Calculate speed and velocity",
    category: "physics",
    subcategory: "Mechanics",
    href: "/calculator/speed-velocity",
    icon: Gauge,
    tags: ["speed", "velocity", "distance", "time", "motion"]
  },
  {
    id: "free-fall",
    title: "Free Fall Calculator",
    description: "Calculate free fall motion under gravity",
    category: "physics",
    subcategory: "Mechanics",
    href: "/calculator/free-fall",
    icon: Activity,
    tags: ["free fall", "gravity", "acceleration", "height", "time"]
  },
  {
    id: "wave-speed",
    title: "Wave Speed Calculator",
    description: "Calculate wave speed, frequency, and wavelength",
    category: "physics",
    subcategory: "Waves",
    href: "/calculator/wave-speed",
    icon: Waves,
    tags: ["wave", "frequency", "wavelength", "speed", "period"]
  },
  {
    id: "frequency-wavelength",
    title: "Frequency & Wavelength Calculator",
    description: "Convert between frequency and wavelength",
    category: "physics",
    subcategory: "Waves",
    href: "/calculator/frequency-wavelength",
    icon: Waves,
    tags: ["frequency", "wavelength", "wave", "electromagnetic", "light"]
  },
  {
    id: "doppler-effect",
    title: "Doppler Effect Calculator",
    description: "Calculate frequency shifts due to motion",
    category: "physics",
    subcategory: "Waves",
    href: "/calculator/doppler-effect",
    icon: Waves,
    tags: ["doppler", "frequency", "wave", "motion", "sound"]
  },
  {
    id: "temperature-converter",
    title: "Temperature Converter",
    description: "Convert between Celsius, Fahrenheit, and Kelvin",
    category: "physics",
    subcategory: "Thermodynamics",
    href: "/calculator/temperature-converter",
    icon: Thermometer,
    tags: ["temperature", "celsius", "fahrenheit", "kelvin", "conversion"]
  },
  {
    id: "heat-transfer",
    title: "Heat Transfer Calculator",
    description: "Calculate heat transfer and thermal energy",
    category: "physics",
    subcategory: "Thermodynamics",
    href: "/calculator/heat-transfer",
    icon: Thermometer,
    tags: ["heat", "thermal", "energy", "temperature", "specific heat"]
  },
  {
    id: "thermal-expansion",
    title: "Thermal Expansion Calculator",
    description: "Calculate linear and volumetric expansion",
    category: "physics",
    subcategory: "Thermodynamics",
    href: "/calculator/thermal-expansion",
    icon: Thermometer,
    tags: ["thermal", "expansion", "temperature", "volume", "linear"]
  },
  {
    id: "circuit-power",
    title: "Circuit Power Calculator",
    description: "Calculate power in electrical circuits",
    category: "physics",
    subcategory: "Electricity",
    href: "/calculator/circuit-power",
    icon: CircuitBoard,
    tags: ["circuit", "power", "voltage", "current", "electricity"]
  },
  {
    id: "resistor-color",
    title: "Resistor Color Code Calculator",
    description: "Decode resistor color bands",
    category: "physics",
    subcategory: "Electricity",
    href: "/calculator/resistor-color",
    icon: CircuitBoard,
    tags: ["resistor", "color code", "resistance", "electronics"]
  },
  {
    id: "resistance-calculator",
    title: "Resistance Calculator",
    description: "Calculate total resistance in series and parallel",
    category: "physics",
    subcategory: "Electricity",
    href: "/calculator/resistance-calculator",
    icon: CircuitBoard,
    tags: ["resistance", "series", "parallel", "circuit", "ohm"]
  },
  {
    id: "capacitance",
    title: "Capacitance Calculator",
    description: "Calculate capacitance and charge storage",
    category: "physics",
    subcategory: "Electricity",
    href: "/calculator/capacitance",
    icon: Battery,
    tags: ["capacitance", "capacitor", "charge", "voltage", "farad"]
  },
  {
    id: "rotational-motion",
    title: "Rotational Motion Calculator",
    description: "Calculate angular velocity and acceleration",
    category: "physics",
    subcategory: "Mechanics",
    href: "/calculator/rotational-motion",
    icon: Activity,
    tags: ["rotation", "angular", "velocity", "acceleration", "torque"]
  },
  {
    id: "moment-inertia",
    title: "Moment of Inertia Calculator",
    description: "Calculate rotational inertia for various shapes",
    category: "physics",
    subcategory: "Mechanics",
    href: "/calculator/moment-inertia",
    icon: Activity,
    tags: ["inertia", "rotation", "mass", "moment", "angular"]
  },
  {
    id: "center-mass",
    title: "Center of Mass Calculator",
    description: "Find the center of mass of a system",
    category: "physics",
    subcategory: "Mechanics",
    href: "/calculator/center-mass",
    icon: Activity,
    tags: ["center of mass", "balance", "mass", "position"]
  },
  {
    id: "coulombs-law",
    title: "Coulomb's Law Calculator",
    description: "Calculate electrostatic force between charges",
    category: "physics",
    subcategory: "Electricity",
    href: "/calculator/coulombs-law",
    icon: Zap,
    tags: ["coulomb", "charge", "force", "electric", "electrostatic"]
  },
  {
    id: "electric-field",
    title: "Electric Field Calculator",
    description: "Calculate electric field strength",
    category: "physics",
    subcategory: "Electricity",
    href: "/calculator/electric-field",
    icon: Zap,
    tags: ["electric field", "charge", "force", "voltage"]
  },
  {
    id: "magnetic-force",
    title: "Magnetic Force Calculator",
    description: "Calculate force on moving charges in magnetic fields",
    category: "physics",
    subcategory: "Magnetism",
    href: "/calculator/magnetic-force",
    icon: Zap,
    tags: ["magnetic", "force", "charge", "field", "lorentz"]
  },
  {
    id: "faradays-law",
    title: "Faraday's Law Calculator",
    description: "Calculate induced EMF and magnetic flux",
    category: "physics",
    subcategory: "Magnetism",
    href: "/calculator/faradays-law",
    icon: Zap,
    tags: ["faraday", "induction", "emf", "magnetic flux", "electromagnetic"]
  },
  {
    id: "rlc-circuit",
    title: "RLC Circuit Calculator",
    description: "Analyze RLC circuits and resonance",
    category: "physics",
    subcategory: "Electricity",
    href: "/calculator/rlc-circuit",
    icon: CircuitBoard,
    tags: ["rlc", "circuit", "resonance", "impedance", "ac"]
  },
  {
    id: "van-der-waals",
    title: "Van der Waals Calculator",
    description: "Calculate real gas behavior",
    category: "physics",
    subcategory: "Thermodynamics",
    href: "/calculator/van-der-waals",
    icon: Wind,
    tags: ["van der waals", "gas", "pressure", "volume", "real gas"]
  },
  {
    id: "entropy-change",
    title: "Entropy Change Calculator",
    description: "Calculate entropy changes in thermodynamic processes",
    category: "physics",
    subcategory: "Thermodynamics",
    href: "/calculator/entropy-change",
    icon: Thermometer,
    tags: ["entropy", "thermodynamics", "disorder", "heat", "temperature"]
  },
  {
    id: "carnot-efficiency",
    title: "Carnot Efficiency Calculator",
    description: "Calculate maximum efficiency of heat engines",
    category: "physics",
    subcategory: "Thermodynamics",
    href: "/calculator/carnot-efficiency",
    icon: Thermometer,
    tags: ["carnot", "efficiency", "heat engine", "temperature", "thermodynamics"]
  },
  {
    id: "time-dilation",
    title: "Time Dilation Calculator",
    description: "Calculate relativistic time dilation effects",
    category: "physics",
    subcategory: "Relativity",
    href: "/calculator/time-dilation",
    icon: Activity,
    tags: ["time dilation", "relativity", "speed of light", "einstein"]
  },
  {
    id: "length-contraction",
    title: "Length Contraction Calculator",
    description: "Calculate relativistic length contraction",
    category: "physics",
    subcategory: "Relativity",
    href: "/calculator/length-contraction",
    icon: Ruler,
    tags: ["length contraction", "relativity", "lorentz", "einstein"]
  },
  {
    id: "relativistic-energy",
    title: "Relativistic Energy Calculator",
    description: "Calculate energy at relativistic speeds",
    category: "physics",
    subcategory: "Relativity",
    href: "/calculator/relativistic-energy",
    icon: Zap,
    tags: ["energy", "relativity", "mass", "e=mc2", "einstein"]
  },
  {
    id: "de-broglie",
    title: "De Broglie Wavelength Calculator",
    description: "Calculate matter wave wavelength",
    category: "physics",
    subcategory: "Quantum",
    href: "/calculator/de-broglie",
    icon: Waves,
    tags: ["de broglie", "wavelength", "quantum", "momentum", "wave-particle"]
  },
  {
    id: "photoelectric-effect",
    title: "Photoelectric Effect Calculator",
    description: "Calculate photon energy and electron emission",
    category: "physics",
    subcategory: "Quantum",
    href: "/calculator/photoelectric-effect",
    icon: Lightbulb,
    tags: ["photoelectric", "photon", "electron", "work function", "quantum"]
  },
  {
    id: "heisenberg-uncertainty",
    title: "Heisenberg Uncertainty Calculator",
    description: "Calculate quantum uncertainty limits",
    category: "physics",
    subcategory: "Quantum",
    href: "/calculator/heisenberg-uncertainty",
    icon: Activity,
    tags: ["heisenberg", "uncertainty", "quantum", "momentum", "position"]
  },
  {
    id: "lens-equation",
    title: "Lens Equation Calculator",
    description: "Calculate focal length and image properties",
    category: "physics",
    subcategory: "Optics",
    href: "/calculator/lens-equation",
    icon: Activity,
    tags: ["lens", "focal length", "optics", "image", "magnification"]
  },
  {
    id: "mirror-equation",
    title: "Mirror Equation Calculator",
    description: "Calculate mirror focal length and images",
    category: "physics",
    subcategory: "Optics",
    href: "/calculator/mirror-equation",
    icon: Activity,
    tags: ["mirror", "focal length", "optics", "reflection", "image"]
  },
  {
    id: "snells-law",
    title: "Snell's Law Calculator",
    description: "Calculate refraction angles",
    category: "physics",
    subcategory: "Optics",
    href: "/calculator/snells-law",
    icon: Activity,
    tags: ["snell", "refraction", "light", "index", "angle"]
  },
  {
    id: "diffraction-grating",
    title: "Diffraction Grating Calculator",
    description: "Calculate diffraction patterns and angles",
    category: "physics",
    subcategory: "Optics",
    href: "/calculator/diffraction-grating",
    icon: Waves,
    tags: ["diffraction", "grating", "light", "wavelength", "interference"]
  },

  // Chemistry Calculators
  {
    id: "molarity",
    title: "Molarity Calculator",
    description: "Calculate molar concentration of solutions",
    category: "chemistry",
    subcategory: "Solutions",
    href: "/calculator/molarity",
    icon: FlaskConical,
    tags: ["molarity", "concentration", "moles", "volume", "solution"]
  },
  {
    id: "dilution",
    title: "Dilution Calculator",
    description: "Calculate dilution ratios and concentrations",
    category: "chemistry",
    subcategory: "Solutions",
    href: "/calculator/dilution",
    icon: Droplet,
    tags: ["dilution", "concentration", "volume", "solution", "m1v1=m2v2"]
  },
  {
    id: "mass-to-moles",
    title: "Mass to Moles Converter",
    description: "Convert mass to moles using molar mass",
    category: "chemistry",
    subcategory: "Stoichiometry",
    href: "/calculator/mass-to-moles",
    icon: TestTube,
    tags: ["mass", "moles", "molar mass", "conversion", "stoichiometry"]
  },
  {
    id: "moles-to-mass",
    title: "Moles to Mass Converter",
    description: "Convert moles to mass using molar mass",
    category: "chemistry",
    subcategory: "Stoichiometry",
    href: "/calculator/moles-to-mass",
    icon: TestTube,
    tags: ["moles", "mass", "molar mass", "conversion", "stoichiometry"]
  },
  {
    id: "percent-composition",
    title: "Percent Composition Calculator",
    description: "Calculate percentage of each element in a compound",
    category: "chemistry",
    subcategory: "Stoichiometry",
    href: "/calculator/percent-composition",
    icon: Beaker,
    tags: ["percent composition", "formula", "elements", "mass percent"]
  },
  {
    id: "limiting-reactant",
    title: "Limiting Reactant Calculator",
    description: "Identify limiting and excess reactants",
    category: "chemistry",
    subcategory: "Stoichiometry",
    href: "/calculator/limiting-reactant",
    icon: TestTube,
    tags: ["limiting reactant", "stoichiometry", "reaction", "moles"]
  },
  {
    id: "percent-yield",
    title: "Percent Yield Calculator",
    description: "Calculate reaction efficiency and yield",
    category: "chemistry",
    subcategory: "Stoichiometry",
    href: "/calculator/percent-yield",
    icon: Beaker,
    tags: ["percent yield", "actual yield", "theoretical yield", "efficiency"]
  },
  {
    id: "empirical-formula",
    title: "Empirical Formula Calculator",
    description: "Determine simplest formula from composition",
    category: "chemistry",
    subcategory: "Stoichiometry",
    href: "/calculator/empirical-formula",
    icon: Atom,
    tags: ["empirical formula", "composition", "ratio", "elements"]
  },
  {
    id: "molecular-formula",
    title: "Molecular Formula Calculator",
    description: "Find molecular formula from empirical formula",
    category: "chemistry",
    subcategory: "Stoichiometry",
    href: "/calculator/molecular-formula",
    icon: Atom,
    tags: ["molecular formula", "empirical formula", "molar mass"]
  },
  {
    id: "ph",
    title: "pH Calculator",
    description: "Calculate pH from hydrogen ion concentration",
    category: "chemistry",
    subcategory: "Acids & Bases",
    href: "/calculator/ph",
    icon: Droplet,
    tags: ["ph", "acid", "base", "hydrogen ion", "concentration"]
  },
  {
    id: "poh",
    title: "pOH Calculator",
    description: "Calculate pOH from hydroxide ion concentration",
    category: "chemistry",
    subcategory: "Acids & Bases",
    href: "/calculator/poh",
    icon: Droplet,
    tags: ["poh", "hydroxide", "base", "concentration"]
  },
  {
    id: "hydrogen-ion",
    title: "Hydrogen Ion Calculator",
    description: "Calculate H+ concentration from pH",
    category: "chemistry",
    subcategory: "Acids & Bases",
    href: "/calculator/hydrogen-ion",
    icon: Droplet,
    tags: ["hydrogen ion", "h+", "ph", "concentration", "acid"]
  },
  {
    id: "buffer-solution",
    title: "Buffer Solution Calculator",
    description: "Calculate buffer pH and composition",
    category: "chemistry",
    subcategory: "Acids & Bases",
    href: "/calculator/buffer-solution",
    icon: Beaker,
    tags: ["buffer", "ph", "henderson-hasselbalch", "acid", "base"]
  },
  {
    id: "ideal-gas-law",
    title: "Ideal Gas Law Calculator",
    description: "Calculate gas properties using PV=nRT",
    category: "chemistry",
    subcategory: "Gases",
    href: "/calculator/ideal-gas-law",
    icon: Wind,
    tags: ["ideal gas", "pressure", "volume", "temperature", "pv=nrt"]
  },
  {
    id: "boyles-law",
    title: "Boyle's Law Calculator",
    description: "Calculate pressure-volume relationships",
    category: "chemistry",
    subcategory: "Gases",
    href: "/calculator/boyles-law",
    icon: Wind,
    tags: ["boyle", "pressure", "volume", "gas law"]
  },
  {
    id: "charles-law",
    title: "Charles's Law Calculator",
    description: "Calculate volume-temperature relationships",
    category: "chemistry",
    subcategory: "Gases",
    href: "/calculator/charles-law",
    icon: Wind,
    tags: ["charles", "volume", "temperature", "gas law"]
  },
  {
    id: "combined-gas-law",
    title: "Combined Gas Law Calculator",
    description: "Calculate combined gas properties",
    category: "chemistry",
    subcategory: "Gases",
    href: "/calculator/combined-gas-law",
    icon: Wind,
    tags: ["combined gas law", "pressure", "volume", "temperature"]
  },
  {
    id: "stoichiometry",
    title: "Stoichiometry Calculator",
    description: "Calculate mole ratios and conversions",
    category: "chemistry",
    subcategory: "Stoichiometry",
    href: "/calculator/stoichiometry",
    icon: TestTube,
    tags: ["stoichiometry", "moles", "ratio", "reaction", "conversion"]
  },
  {
    id: "equilibrium-constant",
    title: "Equilibrium Constant Calculator",
    description: "Calculate Keq for chemical equilibrium",
    category: "chemistry",
    subcategory: "Equilibrium",
    href: "/calculator/equilibrium-constant",
    icon: Beaker,
    tags: ["equilibrium", "constant", "keq", "concentration"]
  },
  {
    id: "reaction-quotient",
    title: "Reaction Quotient Calculator",
    description: "Calculate Q and predict reaction direction",
    category: "chemistry",
    subcategory: "Equilibrium",
    href: "/calculator/reaction-quotient",
    icon: Beaker,
    tags: ["reaction quotient", "q", "equilibrium", "direction"]
  },
  {
    id: "ice-table",
    title: "ICE Table Solver",
    description: "Solve equilibrium problems using ICE tables",
    category: "chemistry",
    subcategory: "Equilibrium",
    href: "/calculator/ice-table",
    icon: TestTube,
    tags: ["ice table", "equilibrium", "initial", "change", "equilibrium"]
  },
  {
    id: "enthalpy-change",
    title: "Enthalpy Change Calculator",
    description: "Calculate heat of reaction and enthalpy",
    category: "chemistry",
    subcategory: "Thermochemistry",
    href: "/calculator/enthalpy-change",
    icon: Thermometer,
    tags: ["enthalpy", "heat", "reaction", "delta h", "thermochemistry"]
  },
  {
    id: "hess-law",
    title: "Hess's Law Calculator",
    description: "Calculate enthalpy using Hess's Law",
    category: "chemistry",
    subcategory: "Thermochemistry",
    href: "/calculator/hess-law",
    icon: Thermometer,
    tags: ["hess law", "enthalpy", "heat", "thermochemistry"]
  },
  {
    id: "gibbs-free-energy",
    title: "Gibbs Free Energy Calculator",
    description: "Calculate spontaneity and free energy",
    category: "chemistry",
    subcategory: "Thermochemistry",
    href: "/calculator/gibbs-free-energy",
    icon: Thermometer,
    tags: ["gibbs", "free energy", "spontaneity", "delta g", "entropy"]
  },
  {
    id: "nernst-equation",
    title: "Nernst Equation Calculator",
    description: "Calculate cell potential under non-standard conditions",
    category: "chemistry",
    subcategory: "Electrochemistry",
    href: "/calculator/nernst-equation",
    icon: Battery,
    tags: ["nernst", "cell potential", "electrochemistry", "voltage"]
  },
  {
    id: "cell-potential",
    title: "Cell Potential Calculator",
    description: "Calculate electrochemical cell voltage",
    category: "chemistry",
    subcategory: "Electrochemistry",
    href: "/calculator/cell-potential",
    icon: Battery,
    tags: ["cell potential", "voltage", "electrochemistry", "redox"]
  },
  {
    id: "electron-configuration",
    title: "Electron Configuration Tool",
    description: "Generate electron configurations for elements",
    category: "chemistry",
    subcategory: "Atomic Structure",
    href: "/calculator/electron-configuration",
    icon: Atom,
    tags: ["electron configuration", "orbital", "element", "atomic"]
  },
  {
    id: "orbital-diagram",
    title: "Orbital Diagram Generator",
    description: "Create orbital diagrams for elements",
    category: "chemistry",
    subcategory: "Atomic Structure",
    href: "/calculator/orbital-diagram",
    icon: Atom,
    tags: ["orbital diagram", "electron", "spin", "atomic structure"]
  },
  {
    id: "rate-law",
    title: "Rate Law Calculator",
    description: "Calculate reaction rates and orders",
    category: "chemistry",
    subcategory: "Kinetics",
    href: "/calculator/rate-law",
    icon: Activity,
    tags: ["rate law", "reaction rate", "kinetics", "order"]
  },
  {
    id: "half-life",
    title: "Half-Life Calculator",
    description: "Calculate radioactive decay and half-lives",
    category: "chemistry",
    subcategory: "Kinetics",
    href: "/calculator/half-life",
    icon: Activity,
    tags: ["half life", "decay", "radioactive", "kinetics", "time"]
  },
  {
    id: "activation-energy",
    title: "Activation Energy Calculator",
    description: "Calculate activation energy using Arrhenius equation",
    category: "chemistry",
    subcategory: "Kinetics",
    href: "/calculator/activation-energy",
    icon: Thermometer,
    tags: ["activation energy", "arrhenius", "kinetics", "temperature"]
  },

  // Math Calculators
  {
    id: "basic-arithmetic",
    title: "Basic Arithmetic Calculator",
    description: "Perform basic math operations",
    category: "math",
    subcategory: "Basic Math",
    href: "/calculator/basic-arithmetic",
    icon: Calculator,
    tags: ["arithmetic", "addition", "subtraction", "multiplication", "division"]
  },
  {
    id: "percentage",
    title: "Percentage Calculator",
    description: "Calculate percentages and percent changes",
    category: "math",
    subcategory: "Basic Math",
    href: "/calculator/percentage",
    icon: Percent,
    tags: ["percentage", "percent", "increase", "decrease", "change"]
  },
  {
    id: "fraction",
    title: "Fraction Calculator",
    description: "Add, subtract, multiply, and divide fractions",
    category: "math",
    subcategory: "Basic Math",
    href: "/calculator/fraction",
    icon: Calculator,
    tags: ["fraction", "numerator", "denominator", "rational"]
  },
  {
    id: "exponent-root",
    title: "Exponent & Root Calculator",
    description: "Calculate powers and roots",
    category: "math",
    subcategory: "Basic Math",
    href: "/calculator/exponent-root",
    icon: Calculator,
    tags: ["exponent", "power", "root", "square root", "cube root"]
  },
  {
    id: "linear-equation",
    title: "Linear Equation Solver",
    description: "Solve linear equations",
    category: "math",
    subcategory: "Algebra",
    href: "/calculator/linear-equation",
    icon: AlignLeft,
    tags: ["linear", "equation", "solve", "algebra", "x"]
  },
  {
    id: "quadratic-equation",
    title: "Quadratic Equation Solver",
    description: "Solve quadratic equations using formula",
    category: "math",
    subcategory: "Algebra",
    href: "/calculator/quadratic-equation",
    icon: AlignLeft,
    tags: ["quadratic", "equation", "solve", "formula", "roots"]
  },
  {
    id: "area-perimeter",
    title: "Area & Perimeter Calculator",
    description: "Calculate area and perimeter of shapes",
    category: "math",
    subcategory: "Geometry",
    href: "/calculator/area-perimeter",
    icon: Ruler,
    tags: ["area", "perimeter", "shape", "geometry", "square", "rectangle"]
  },
  {
    id: "volume",
    title: "Volume Calculator",
    description: "Calculate volume of 3D shapes",
    category: "math",
    subcategory: "Geometry",
    href: "/calculator/volume",
    icon: Ruler,
    tags: ["volume", "3d", "shape", "geometry", "cube", "sphere"]
  },
  {
    id: "pythagorean",
    title: "Pythagorean Calculator",
    description: "Calculate sides of right triangles",
    category: "math",
    subcategory: "Geometry",
    href: "/calculator/pythagorean",
    icon: Triangle,
    tags: ["pythagorean", "triangle", "hypotenuse", "right triangle", "a2+b2=c2"]
  },
  {
    id: "trig-functions",
    title: "Trigonometric Functions Calculator",
    description: "Calculate sine, cosine, tangent, and more",
    category: "math",
    subcategory: "Trigonometry",
    href: "/calculator/trig-functions",
    icon: Compass,
    tags: ["trigonometry", "sine", "cosine", "tangent", "angle"]
  },
  {
    id: "matrix",
    title: "Matrix Calculator",
    description: "Perform matrix operations",
    category: "math",
    subcategory: "Linear Algebra",
    href: "/calculator/matrix",
    icon: Grid3x3,
    tags: ["matrix", "determinant", "inverse", "multiplication", "linear algebra"]
  },
  {
    id: "mean-median-mode",
    title: "Mean, Median, Mode Calculator",
    description: "Calculate measures of central tendency",
    category: "math",
    subcategory: "Statistics",
    href: "/calculator/mean-median-mode",
    icon: BarChart3,
    tags: ["mean", "median", "mode", "average", "statistics"]
  },
  {
    id: "standard-deviation",
    title: "Standard Deviation Calculator",
    description: "Calculate standard deviation and variance",
    category: "math",
    subcategory: "Statistics",
    href: "/calculator/standard-deviation",
    icon: TrendingUp,
    tags: ["standard deviation", "variance", "statistics", "spread"]
  },
  {
    id: "z-score",
    title: "Z-Score Calculator",
    description: "Calculate z-scores and probabilities",
    category: "math",
    subcategory: "Statistics",
    href: "/calculator/z-score",
    icon: BarChart3,
    tags: ["z-score", "standard score", "statistics", "normal distribution"]
  },
  {
    id: "permutation-combination",
    title: "Permutation & Combination Calculator",
    description: "Calculate arrangements and selections",
    category: "math",
    subcategory: "Statistics",
    href: "/calculator/permutation-combination",
    icon: PieChart,
    tags: ["permutation", "combination", "factorial", "counting", "probability"]
  },

  // Tools
  {
    id: "unit-converter",
    title: "Unit Converter",
    description: "Convert between different units",
    category: "tools",
    href: "/calculator/unit-converter",
    icon: Ruler,
    tags: ["unit", "converter", "conversion", "metric", "imperial"]
  },
  {
    id: "scientific-notation",
    title: "Scientific Notation Converter",
    description: "Convert to and from scientific notation",
    category: "tools",
    href: "/calculator/scientific-notation",
    icon: Calculator,
    tags: ["scientific notation", "exponent", "conversion", "standard form"]
  },
  {
    id: "significant-figures",
    title: "Significant Figures Tool",
    description: "Count and round to significant figures",
    category: "tools",
    href: "/calculator/significant-figures",
    icon: Calculator,
    tags: ["significant figures", "sig figs", "precision", "rounding"]
  },
  {
    id: "latex-formatter",
    title: "LaTeX Formatter",
    description: "Format mathematical expressions in LaTeX",
    category: "tools",
    href: "/calculator/latex-formatter",
    icon: AlignLeft,
    tags: ["latex", "format", "math", "equation", "typesetting"]
  },
  // Finance Calculators - Basic
  {
    id: "simple-interest",
    title: "Simple Interest Calculator",
    description: "Calculate interest earned or owed on principal amount",
    category: "finance",
    subcategory: "Basic Finance",
    href: "/calculator/simple-interest",
    icon: DollarSign,
    tags: ["interest", "principal", "rate", "time", "simple"]
  },
  {
    id: "compound-interest",
    title: "Compound Interest Calculator",
    description: "Calculate interest with compounding periods",
    category: "finance",
    subcategory: "Basic Finance",
    href: "/calculator/compound-interest",
    icon: TrendingUp,
    tags: ["compound", "interest", "investment", "growth", "compounding"]
  },
  {
    id: "mortgage",
    title: "Mortgage Calculator",
    description: "Calculate monthly payments, total interest, and amortization",
    category: "finance",
    subcategory: "Loans",
    href: "/calculator/mortgage",
    icon: Building,
    tags: ["mortgage", "loan", "payment", "amortization", "interest"]
  },
  {
    id: "loan-payment",
    title: "Loan Payment Calculator",
    description: "Calculate monthly loan payments with principal and interest",
    category: "finance",
    subcategory: "Loans",
    href: "/calculator/loan-payment",
    icon: CreditCard,
    tags: ["loan", "payment", "monthly", "installment", "debt"]
  },
  {
    id: "loan-amortization",
    title: "Loan Amortization Calculator",
    description: "Detailed payment breakdown over loan term",
    category: "finance",
    subcategory: "Loans",
    href: "/calculator/loan-amortization",
    icon: LineChart,
    tags: ["amortization", "schedule", "loan", "payment", "principal"]
  },
  {
    id: "apr",
    title: "APR Calculator",
    description: "Calculate Annual Percentage Rate with fees",
    category: "finance",
    subcategory: "Loans",
    href: "/calculator/apr",
    icon: Percent,
    tags: ["apr", "annual", "percentage", "rate", "fees"]
  },
  {
    id: "roi",
    title: "ROI Calculator",
    description: "Calculate Return on Investment percentage",
    category: "finance",
    subcategory: "Basic",
    href: "/calculator/roi",
    icon: TrendingUp,
    tags: ["roi", "return", "investment", "profit", "percentage"]
  },
  {
    id: "present-value",
    title: "Present Value Calculator",
    description: "Calculate current value of future cash flows",
    category: "finance",
    subcategory: "Advanced",
    href: "/calculator/present-value",
    icon: DollarSign,
    tags: ["present", "value", "pv", "discount", "future"]
  },
  {
    id: "future-value",
    title: "Future Value Calculator",
    description: "Calculate future value of current investment",
    category: "finance",
    subcategory: "Basic",
    href: "/calculator/future-value",
    icon: TrendingUp,
    tags: ["future", "value", "fv", "investment", "growth"]
  },
  {
    id: "savings",
    title: "Savings Calculator",
    description: "Calculate monthly savings needed to reach a goal",
    category: "finance",
    subcategory: "Basic",
    href: "/calculator/savings",
    icon: PiggyBank,
    tags: ["savings", "goal", "monthly", "target", "accumulation"]
  },
  {
    id: "break-even",
    title: "Break-Even Analysis",
    description: "Calculate when revenue equals costs",
    category: "finance",
    subcategory: "Basic",
    href: "/calculator/break-even",
    icon: BarChart3,
    tags: ["break-even", "revenue", "costs", "profit", "analysis"]
  },
  {
    id: "profit-margin",
    title: "Profit Margin Calculator",
    description: "Calculate gross and net profit margins",
    category: "finance",
    subcategory: "Basic",
    href: "/calculator/profit-margin",
    icon: Percent,
    tags: ["profit", "margin", "gross", "net", "percentage"]
  },
  {
    id: "depreciation",
    title: "Depreciation Calculator",
    description: "Calculate asset value decline over time",
    category: "finance",
    subcategory: "Basic",
    href: "/calculator/depreciation",
    icon: TrendingDown,
    tags: ["depreciation", "asset", "value", "decline", "amortization"]
  },
  // Finance Calculators - Advanced
  {
    id: "black-scholes",
    title: "Black-Scholes Calculator",
    description: "European option pricing model",
    category: "finance",
    subcategory: "Advanced",
    href: "/calculator/black-scholes",
    icon: LineChart,
    tags: ["black-scholes", "options", "pricing", "derivatives", "call", "put"]
  },
  {
    id: "bond-yield",
    title: "Bond Yield Calculator",
    description: "Calculate YTM, YTC, and current yield",
    category: "finance",
    subcategory: "Investments",
    href: "/calculator/bond-yield",
    icon: Coins,
    tags: ["bond", "yield", "ytm", "ytc", "maturity"]
  },
  {
    id: "duration-convexity",
    title: "Duration & Convexity",
    description: "Measure bond price sensitivity to interest rates",
    category: "finance",
    subcategory: "Advanced",
    href: "/calculator/duration-convexity",
    icon: LineChart,
    tags: ["duration", "convexity", "bond", "sensitivity", "interest"]
  },
  {
    id: "capm",
    title: "CAPM Calculator",
    description: "Expected return using Capital Asset Pricing Model",
    category: "finance",
    subcategory: "Advanced",
    href: "/calculator/capm",
    icon: TrendingUp,
    tags: ["capm", "expected", "return", "beta", "risk"]
  },
  {
    id: "sharpe-ratio",
    title: "Sharpe Ratio Calculator",
    description: "Calculate risk-adjusted returns",
    category: "finance",
    subcategory: "Advanced",
    href: "/calculator/sharpe-ratio",
    icon: BarChart3,
    tags: ["sharpe", "ratio", "risk", "adjusted", "return"]
  },
  {
    id: "var",
    title: "Value at Risk (VaR)",
    description: "Portfolio risk estimation",
    category: "finance",
    subcategory: "Advanced",
    href: "/calculator/var",
    icon: TrendingDown,
    tags: ["var", "value", "risk", "portfolio", "loss"]
  },
  {
    id: "annuity-pv",
    title: "Present Value of Annuity",
    description: "Calculate PV of regular payment streams",
    category: "finance",
    subcategory: "Advanced",
    href: "/calculator/annuity-pv",
    icon: Wallet,
    tags: ["annuity", "present", "value", "payment", "stream"]
  },
  {
    id: "irr",
    title: "Internal Rate of Return",
    description: "Calculate IRR for investment profitability",
    category: "finance",
    subcategory: "Advanced",
    href: "/calculator/irr",
    icon: TrendingUp,
    tags: ["irr", "internal", "rate", "return", "investment"]
  },
  {
    id: "npv",
    title: "Net Present Value",
    description: "Calculate NPV with discount rate",
    category: "finance",
    subcategory: "Advanced",
    href: "/calculator/npv",
    icon: DollarSign,
    tags: ["npv", "net", "present", "value", "discount"]
  },
  {
    id: "modified-duration",
    title: "Modified Duration",
    description: "Interest rate risk measure for bonds",
    category: "finance",
    subcategory: "Advanced",
    href: "/calculator/modified-duration",
    icon: LineChart,
    tags: ["modified", "duration", "bond", "risk", "interest"]
  },
  {
    id: "option-greeks",
    title: "Option Greeks Calculator",
    description: "Calculate Delta, Gamma, Vega, Theta, Rho",
    category: "finance",
    subcategory: "Advanced",
    href: "/calculator/option-greeks",
    icon: Calculator,
    tags: ["greeks", "delta", "gamma", "vega", "theta", "options"]
  },
  {
    id: "binomial-options",
    title: "Binomial Options Pricing",
    description: "American option valuation model",
    category: "finance",
    subcategory: "Advanced",
    href: "/calculator/binomial-options",
    icon: Grid3x3,
    tags: ["binomial", "options", "pricing", "american", "tree"]
  },
  {
    id: "time-value-money",
    title: "Time Value of Money",
    description: "Comprehensive TVM calculations",
    category: "finance",
    subcategory: "Advanced",
    href: "/calculator/time-value-money",
    icon: CalcIcon,
    tags: ["tvm", "time", "value", "money", "pv", "fv"]
  },
  {
    id: "actuarial-pv",
    title: "Actuarial Present Value",
    description: "Life insurance and annuity valuations",
    category: "finance",
    subcategory: "Advanced",
    href: "/calculator/actuarial-pv",
    icon: Wallet,
    tags: ["actuarial", "present", "value", "insurance", "annuity"]
  },
  {
    id: "life-table",
    title: "Life Table Calculator",
    description: "Mortality rates and life expectancy",
    category: "finance",
    subcategory: "Advanced",
    href: "/calculator/life-table",
    icon: BarChart3,
    tags: ["life", "table", "mortality", "expectancy", "actuarial"]
  },
  {
    id: "portfolio-beta",
    title: "Portfolio Beta Calculator",
    description: "Measure systematic risk of portfolio",
    category: "finance",
    subcategory: "Advanced",
    href: "/calculator/portfolio-beta",
    icon: LineChart,
    tags: ["portfolio", "beta", "systematic", "risk", "market"]
  },
  // General Calculators - Health & Body
  {
    id: "bmi",
    title: "BMI Calculator",
    description: "Calculate your Body Mass Index (BMI) using height and weight",
    category: "general",
    subcategory: "Health",
    href: "/calculator/bmi",
    icon: Scale,
    tags: ["bmi", "body mass index", "health", "weight", "obesity", "fitness"]
  },
  {
    id: "bmr",
    title: "BMR Calculator",
    description: "Calculate Basal Metabolic Rate and daily calorie needs",
    category: "general",
    subcategory: "Health",
    href: "/calculator/bmr",
    icon: Flame,
    tags: ["bmr", "basal metabolic rate", "calories", "metabolism", "tdee", "weight loss"]
  },
  {
    id: "body-fat",
    title: "Body Fat Percentage Calculator",
    description: "Estimate body fat percentage using multiple measurement methods",
    category: "general",
    subcategory: "Health",
    href: "/calculator/body-fat",
    icon: Activity,
    tags: ["body fat", "percentage", "fitness", "health", "lean mass"]
  },
  {
    id: "macro-split",
    title: "Macro Split Calculator",
    description: "Calculate optimal protein, carbs, and fat distribution for your goals",
    category: "general",
    subcategory: "Health",
    href: "/calculator/macro-split",
    icon: PieChart,
    tags: ["macros", "protein", "carbs", "fat", "nutrition", "diet"]
  },
  {
    id: "ideal-weight",
    title: "Ideal Weight Calculator",
    description: "Find your target weight based on height and body type",
    category: "general",
    subcategory: "Health",
    href: "/calculator/ideal-weight",
    icon: Target,
    tags: ["ideal weight", "target weight", "healthy weight", "bmi", "height"]
  },
  // General Calculators - Money & Budget
  {
    id: "tip",
    title: "Tip Calculator",
    description: "Calculate tips and split bills easily for any meal or service",
    category: "general",
    subcategory: "Other",
    href: "/calculator/tip",
    icon: DollarSign,
    tags: ["tip", "gratuity", "bill split", "restaurant", "service"]
  },
  {
    id: "loan-repayment",
    title: "Loan Repayment Calculator",
    description: "Calculate monthly payments and total interest for any loan",
    category: "finance",
    subcategory: "Loans",
    href: "/calculator/loan-repayment",
    icon: Receipt,
    tags: ["loan", "repayment", "interest", "payment", "debt"]
  },
  {
    id: "savings-growth",
    title: "Savings Growth Calculator",
    description: "Calculate compound interest with regular deposits over time",
    category: "general",
    subcategory: "Other",
    href: "/calculator/savings-growth",
    icon: PiggyBank,
    tags: ["savings", "compound interest", "deposits", "growth", "investment"]
  },
  {
    id: "currency-converter",
    title: "Currency Converter",
    description: "Convert between different currencies with current exchange rates",
    category: "general",
    subcategory: "Other",
    href: "/calculator/currency-converter",
    icon: Coins,
    tags: ["currency", "exchange", "conversion", "forex", "money"]
  },
  // General Calculators - Time & Planning
  {
    id: "date-difference",
    title: "Date Difference Calculator",
    description: "Calculate days, weeks, months, and years between two dates",
    category: "general",
    subcategory: "Lifestyle",
    href: "/calculator/date-difference",
    icon: Calendar,
    tags: ["date", "difference", "days between", "duration", "time span"]
  },
  {
    id: "age",
    title: "Age Calculator",
    description: "Calculate your exact age in years, months, days, and more",
    category: "general",
    subcategory: "Lifestyle",
    href: "/calculator/age",
    icon: Cake,
    tags: ["age", "birthday", "years old", "date of birth", "dob"]
  },
  {
    id: "countdown",
    title: "Countdown Calculator",
    description: "Count down days, hours, and minutes until your target date",
    category: "general",
    subcategory: "Lifestyle",
    href: "/calculator/countdown",
    icon: Timer,
    tags: ["countdown", "timer", "days until", "event", "deadline"]
  },
  {
    id: "time-zone",
    title: "Time Zone Converter",
    description: "Convert times across different time zones worldwide",
    category: "general",
    subcategory: "Lifestyle",
    href: "/calculator/time-zone",
    icon: Clock,
    tags: ["time zone", "timezone", "utc", "gmt", "conversion"]
  },
  // General Calculators - Shopping & Lifestyle
  {
    id: "discount",
    title: "Discount Calculator",
    description: "Calculate sale prices, discounts, and savings instantly",
    category: "general",
    subcategory: "Lifestyle",
    href: "/calculator/discount",
    icon: Percent,
    tags: ["discount", "sale", "percentage off", "savings", "price"]
  },
  {
    id: "sales-tax",
    title: "Sales Tax Calculator",
    description: "Add or remove sales tax from prices with customizable rates",
    category: "finance",
    subcategory: "Tax",
    href: "/calculator/sales-tax",
    icon: Receipt,
    tags: ["sales tax", "vat", "gst", "tax", "price"]
  },
  {
    id: "fuel-cost",
    title: "Fuel Cost Calculator",
    description: "Calculate trip fuel costs based on distance, MPG, and gas prices",
    category: "general",
    subcategory: "Lifestyle",
    href: "/calculator/fuel-cost",
    icon: Fuel,
    tags: ["fuel", "gas", "trip cost", "mpg", "distance"]
  },
  {
    id: "cooking-converter",
    title: "Cooking Measurement Converter",
    description: "Convert cups to grams, tablespoons to ml, and more",
    category: "general",
    subcategory: "Lifestyle",
    href: "/calculator/cooking-converter",
    icon: ChefHat,
    tags: ["cooking", "measurements", "cups", "grams", "recipe", "conversion"]
  },
  // General Calculators - Fitness & Activity
  {
    id: "running-pace",
    title: "Running Pace Calculator",
    description: "Convert pace, speed, time, and distance for running workouts",
    category: "general",
    subcategory: "Other",
    href: "/calculator/running-pace",
    icon: Footprints,
    tags: ["running", "pace", "speed", "marathon", "5k", "distance"]
  },
  {
    id: "tdee",
    title: "TDEE Calculator",
    description: "Calculate Total Daily Energy Expenditure with your activity level",
    category: "general",
    subcategory: "Other",
    href: "/calculator/tdee",
    icon: Activity,
    tags: ["tdee", "calories", "energy expenditure", "activity level", "metabolism"]
  }
];

export const categories = [
  { id: "all", name: "All Calculators" },
  { id: "physics", name: "Physics" },
  { id: "chemistry", name: "Chemistry" },
  { id: "math", name: "Math" },
  { id: "finance", name: "Finance" },
  { id: "tools", name: "Tools" },
  { id: "general", name: "General" },
];

export const subcategories: Record<string, string[]> = {
  physics: [
    "Mechanics",
    "Energy",
    "Waves",
    "Thermodynamics",
    "Electricity",
    "Magnetism",
    "Relativity",
    "Quantum",
    "Optics"
  ],
  chemistry: [
    "Solutions",
    "Stoichiometry",
    "Acids & Bases",
    "Gases",
    "Equilibrium",
    "Thermochemistry",
    "Electrochemistry",
    "Atomic Structure",
    "Kinetics"
  ],
  math: [
    "Basic Math",
    "Algebra",
    "Geometry",
    "Trigonometry",
    "Linear Algebra",
    "Statistics"
  ],
  finance: [
    "Basic",
    "Advanced",
    "Loans",
    "Tax"
  ],
  tools: [],
  general: [
    "Health",
    "Lifestyle",
    "Other"
  ]
};
